import React, { useState } from 'react'
import {
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  TouchableOpacity,
  View,
} from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { Screen, Header, Text, Icon } from '@components'
import type { MenuStackParamList } from '../../../navigation/navigator/MenuNavigator'
import { useSettingsNavigation } from './hook'
import { getSettingsStyles } from './style'
import { useScreen, useUpdateProfile, usePremium, useExportContacts, useDeleteAccount } from '@hooks'
import { useLanguage, useAuth } from '@context'
import { useLanguageStore, useCalendarSyncStore } from '@store'
import type { Language, AvailableLanguages } from '@context'
import { getCurrentPosition, showErrorToast } from '@utils'
import { ensurePermission } from '../../../utils/calendarSync/nativeCalendar'
import { DeleteAccountModal } from './components/DeleteAccountModal'

type LanguageOption = {
  value: Language
  labelKey: string
}

const languageOptions: LanguageOption[] = [
  { value: 'auto', labelKey: 'settings.auto' },
  { value: 'fr', labelKey: 'settings.french' },
  { value: 'en', labelKey: 'settings.english' },
  { value: 'es', labelKey: 'settings.spanish' },
]

export const Settings: React.FC = () => {
  const theme = useTheme()
  const styles = getSettingsStyles(theme)
  const { navigation } = useSettingsNavigation()
  const menuNavigation = useNavigation<NativeStackNavigationProp<MenuStackParamList>>()
  const { isWeb } = useScreen()
  const { t } = useLanguage()
  const { language, switchLanguage } = useLanguageStore()
  const { mutate: updateProfile } = useUpdateProfile()
  const [locationStatus, setLocationStatus] = useState<'unknown' | 'enabled' | 'disabled'>(
    'unknown',
  )
  const calendarSyncEnabled = useCalendarSyncStore((s) => s.enabled)
  const setCalendarSyncEnabled = useCalendarSyncStore((s) => s.setEnabled)
  const { isPremium } = usePremium()
  const { exportContacts, isExporting } = useExportContacts()
  const { signOut } = useAuth()
  const { mutateAsync: deleteAccount, isPending: isDeletingAccount } = useDeleteAccount()
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openDeleteModal = (): void => {
    setDeleteError(null)
    setDeleteModalVisible(true)
  }

  const closeDeleteModal = (): void => {
    setDeleteModalVisible(false)
    setDeleteError(null)
  }

  const handleConfirmDelete = async (password: string): Promise<void> => {
    setDeleteError(null)
    try {
      await deleteAccount(password)
      setDeleteModalVisible(false)
      signOut()
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      setDeleteError(
        status === 401
          ? t('settings.deleteAccountWrongPassword')
          : t('settings.deleteAccountError'),
      )
    }
  }

  const onToggleCalendarSync = async (value: boolean): Promise<void> => {
    if (value && Platform.OS !== 'web') {
      const granted = await ensurePermission()
      if (!granted) {
        showErrorToast(t('settings.calendarSyncPermissionDenied'))
        return
      }
    }
    setCalendarSyncEnabled(value)
  }

  const handleLanguageChange = (newLanguage: Language) => {
    switchLanguage(newLanguage)
    // Persist to backend
    const resolvedLang: AvailableLanguages =
      newLanguage === 'auto'
        ? (() => {
            try {
              const RNLocalize = require('react-native-localize')
              const autoLang = RNLocalize.getLocales()[0].languageTag.split('-')[0]
              return (['en', 'fr', 'es'].includes(autoLang) ? autoLang : 'fr') as AvailableLanguages
            } catch {
              return 'fr' as AvailableLanguages
            }
          })()
        : newLanguage
    updateProfile({ language: resolvedLang, _silent: true })
  }

  const handleToggleLocation = async (value: boolean) => {
    // Turning off simply disables location usage from the app.
    if (!value) {
      setLocationStatus('disabled')
      return
    }
    // Optimistically flip the toggle on so it responds immediately, then request
    // the position (which triggers the OS/browser permission prompt).
    setLocationStatus('enabled')
    try {
      const coords = await getCurrentPosition()
      if (!coords) {
        // Permission denied / blocked / unavailable → revert and explain.
        setLocationStatus('disabled')
        showErrorToast(t('settings.locationDenied'))
      }
    } catch {
      setLocationStatus('disabled')
      showErrorToast(t('settings.locationDenied'))
    }
  }

  const handleOpenSystemSettings = () => {
    if (Platform.OS !== 'web' && typeof Linking.openSettings === 'function') {
      Linking.openSettings()
    }
  }

  return (
    <Screen>
      <Header
        title={t('settings.title')}
        iconLeft={isWeb ? undefined : 'arrowBack'}
        onPressLeft={() => navigation.goBack()}
      />

      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Language */}
        <Text type="bodyBold" style={styles.sectionTitle}>
          {t('settings.language')}
        </Text>
        <Text type="bodySmall" style={styles.sectionDescription}>
          {t('settings.languageDescription')}
        </Text>

        <View style={styles.optionsContainer}>
          {languageOptions.map((option) => {
            const isSelected = language === option.value
            return (
              <TouchableOpacity
                key={option.value}
                style={[styles.optionCard, isSelected && styles.optionCardSelected]}
                onPress={() => handleLanguageChange(option.value)}
                activeOpacity={0.7}
              >
                <Text
                  type="body"
                  style={StyleSheet.flatten([
                    styles.optionText,
                    isSelected && styles.optionTextSelected,
                  ])}
                >
                  {t(option.labelKey)}
                </Text>
                {isSelected && <Icon name="check" size={20} color={theme.colors.primary} />}
              </TouchableOpacity>
            )
          })}
        </View>

        {/* Location / geolocation */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('settings.location')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('settings.locationDescription')}
          </Text>
          <View style={styles.optionCard}>
            <View style={styles.locationLabel}>
              <Icon name="mapPin" size={20} color={theme.colors.primary} />
              <View style={styles.locationTextBlock}>
                <Text type="body" style={styles.optionText}>
                  {t('settings.locationToggle')}
                </Text>
                <Text type="bodySmall" style={styles.sectionDescription}>
                  {t(
                    locationStatus === 'enabled'
                      ? 'settings.locationStatusEnabled'
                      : locationStatus === 'disabled'
                        ? 'settings.locationStatusDisabled'
                        : 'settings.locationStatusUnknown',
                  )}
                </Text>
              </View>
            </View>
            <TouchableOpacity
              testID="settings-location-switch"
              activeOpacity={0.8}
              onPress={() => handleToggleLocation(locationStatus !== 'enabled')}
              style={[
                styles.toggleTrack,
                locationStatus === 'enabled' && styles.toggleTrackOn,
              ]}
            >
              <View
                style={[
                  styles.toggleThumb,
                  locationStatus === 'enabled' && styles.toggleThumbOn,
                ]}
              />
            </TouchableOpacity>
          </View>
          {!isWeb && (
            <TouchableOpacity
              style={[styles.optionCard, { marginTop: theme.spacing.sm }]}
              onPress={handleOpenSystemSettings}
              activeOpacity={0.7}
            >
              <Text type="body" style={styles.optionText}>
                {t('geolocation.denied.settings')}
              </Text>
              <Icon name="settings" size={20} color={theme.colors.primary} />
            </TouchableOpacity>
          )}
        </View>

        {/* Contacts export (Premium) */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('export.sectionTitle')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('export.sectionDescription')}
          </Text>
          <TouchableOpacity
            testID="settings-export-contacts"
            style={styles.optionCard}
            onPress={exportContacts}
            disabled={isExporting}
            activeOpacity={0.7}
          >
            <View style={styles.locationLabel}>
              <Icon name="save" size={20} color={theme.colors.primary} />
              <Text type="body" style={styles.optionText}>
                {isExporting ? t('export.exporting') : t('export.cta')}
              </Text>
            </View>
            {!isPremium && <Icon name="star" size={18} color={theme.colors.gold} />}
          </TouchableOpacity>
        </View>

        {/* Calendar sync (native only) */}
        {Platform.OS !== 'web' && (
          <View style={{ marginTop: theme.spacing.xxl }}>
            <Text type="bodyBold" style={styles.sectionTitle}>
              {t('settings.calendarSync')}
            </Text>
            <Text type="bodySmall" style={styles.sectionDescription}>
              {t('settings.calendarSyncDescription')}
            </Text>
            <View style={styles.optionCard}>
              <Text type="body" style={styles.optionText}>
                {t('settings.calendarSyncEnable')}
              </Text>
              <Switch
                testID="settings-calendar-sync-switch"
                value={calendarSyncEnabled}
                onValueChange={onToggleCalendarSync}
              />
            </View>
          </View>
        )}

        {/* Subscription status */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('settings.subscription')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('settings.subscriptionDescription')}
          </Text>
          <TouchableOpacity
            testID="settings-subscription-row"
            style={styles.optionCard}
            activeOpacity={0.7}
            onPress={() => menuNavigation.navigate('Subscription')}
          >
            <Text type="body" style={styles.optionText}>
              {isPremium ? t('settings.subscriptionPremium') : t('settings.subscriptionManage')}
            </Text>
            <Icon name="arrowRight" size={20} color={theme.colors.primary} />
          </TouchableOpacity>
        </View>

        {/* Danger zone — account deletion (App Store guideline 5.1.1(v)) */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('settings.deleteAccountTitle')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('settings.deleteAccountDescription')}
          </Text>
          <TouchableOpacity
            testID="settings-delete-account-row"
            style={styles.optionCard}
            activeOpacity={0.7}
            onPress={openDeleteModal}
          >
            <Text type="body" style={[styles.optionText, { color: theme.colors.error }]}>
              {t('settings.deleteAccountButton')}
            </Text>
            <Icon name="trash" size={20} color={theme.colors.error} />
          </TouchableOpacity>
        </View>
      </ScrollView>

      <DeleteAccountModal
        isVisible={isDeleteModalVisible}
        isDeleting={isDeletingAccount}
        errorMessage={deleteError}
        onCancel={closeDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </Screen>
  )
}
